import { produce } from 'immer';
import {
  UNPROJECTED_TILE_SIZE,
  PROJECTED_TILE_SIZE,
  ZOOM_INCREMENT,
  MAX_ZOOM,
  MIN_ZOOM,
  TEXTBOX_PADDING,
  DEFAULT_FONT_FAMILY,
  TEXTBOX_DEFAULTS,
  TEXTBOX_FONT_WEIGHT,
  DEFAULT_LABEL_HEIGHT,
  PROJECT_BOUNDING_BOX_PADDING,
  FIT_TO_VIEW_TILE_PADDING
} from 'src/config';
import {
  Coords,
  TileOrigin,
  Size,
  Scroll,
  Mouse,
  ItemReference,
  ProjectionOrientationEnum,
  BoundingBox,
  TextBox,
  SlimMouseEvent,
  View,
  AnchorPosition,
  Connector,
  ConnectorAnchor
} from 'src/types';
import {
  CoordsUtils,
  clamp,
  toPx,
} from 'src/utils';
import { useScene } from 'src/hooks/useScene';

// ✨ NEW: Import connection utilities from core module
import {
  normalizePositionFromOrigin as normalizePositionFromOriginCore,
  connectorPathTileToGlobal as connectorPathTileToGlobalCore,
  getAllAnchors as getAllAnchorsCore,
  getAnchorTile as getAnchorTileCore,
  getConnectorPath as getConnectorPathCore,
  getRectangleFromSize as getRectangleFromSizeCore
} from '@core/connections';

// Import grid utilities from core
import {
  getBoundingBox,
  sortByPosition,
  isWithinBounds,
  getGridSubset as getGridSubsetCore
} from '@core/grid/gridUtils';
import { defaultIsometricGrid } from '@core/grid';
import { RenderLayer } from '@core/renderer/types';

// ===================================================================
// RE-EXPORTS FOR BACKWARDS COMPATIBILITY
// These functions now live in core/connections/ but are re-exported
// here so existing code continues to work
// ===================================================================

/**
 * @deprecated Use import from '@core/connections' instead
 * Kept for backwards compatibility
 */
export const normalisePositionFromOrigin = normalizePositionFromOriginCore;

/**
 * @deprecated Use import from '@core/connections' instead
 * Kept for backwards compatibility
 */
export const connectorPathTileToGlobal = connectorPathTileToGlobalCore;

/**
 * @deprecated Use import from '@core/connections' instead
 * Kept for backwards compatibility
 */
export const getAllAnchors = getAllAnchorsCore;

/**
 * @deprecated Use import from '@core/connections' instead
 * Kept for backwards compatibility
 */
export const getAnchorTile = getAnchorTileCore;

/**
 * @deprecated Use import from '@core/connections' instead
 * Kept for backwards compatibility
 */
export const getConnectorPath = getConnectorPathCore;

/**
 * @deprecated Use import from '@core/connections' instead
 * Kept for backwards compatibility
 */
export const getRectangleFromSize = getRectangleFromSizeCore;

/**
 * @deprecated Use import from '@core/grid/gridUtils' instead
 * Kept for backwards compatibility
 */
export const getGridSubset = getGridSubsetCore;

/**
 * @deprecated Use import from '@core/grid/gridUtils' instead
 * Kept for backwards compatibility
 */
export { isWithinBounds };

// ===================================================================
// SCREEN/ISOMETRIC CONVERSION FUNCTIONS
// These remain in utils/renderer.ts as they're general rendering utils
// ===================================================================

interface ScreenToIso {
  mouse: Coords;
  zoom: number;
  scroll: Scroll;
  rendererSize: Size;
}

// converts a mouse position to a tile position
export const screenToIso = ({
  mouse,
  zoom,
  scroll,
  rendererSize
}: ScreenToIso) => {
  return defaultIsometricGrid.screenToTile({
    mouse,
    zoom,
    scroll,
    rendererSize
  });
};

interface GetTilePosition {
  tile: Coords;
  origin?: TileOrigin;
}

export const getTilePosition = ({
  tile,
  origin = 'CENTER'
}: GetTilePosition) => {
  return defaultIsometricGrid.getTilePosition(tile, origin);
};

type IsoToScreen = GetTilePosition & {
  rendererSize: Size;
};

export const isoToScreen = ({ tile, origin, rendererSize }: IsoToScreen) => {
  const position = getTilePosition({ tile, origin });

  return {
    x: position.x + rendererSize.width / 2,
    y: position.y + rendererSize.height / 2
  };
};

// ===================================================================
// ISOMETRIC PROJECTION
// ===================================================================

const isoProjectionBaseValues = [0.707, -0.409, 0.707, 0.409, 0, -0.816];

export const getIsoMatrix = (
  orientation?: keyof typeof ProjectionOrientationEnum
) => {
  switch (orientation) {
    case ProjectionOrientationEnum.Y:
      return produce(isoProjectionBaseValues, (draft) => {
        draft[1] = -draft[1];
        draft[2] = -draft[2];
      });
    case ProjectionOrientationEnum.X:
    default:
      return isoProjectionBaseValues;
  }
};

export const getIsoProjectionCss = (
  orientation?: keyof typeof ProjectionOrientationEnum
) => {
  const matrixTransformValues = getIsoMatrix(orientation);
  return `matrix(${matrixTransformValues.join(', ')})`;
};

export const getTranslateCSS = (translate: Coords = { x: 0, y: 0 }) => {
  return `translate(${translate.x}px, ${translate.y}px)`;
};

// ===================================================================
// ZOOM CONTROLS
// ===================================================================

export const incrementZoom = (zoom: number) => {
  const newZoom = clamp(zoom + ZOOM_INCREMENT, MIN_ZOOM, MAX_ZOOM);
  return Math.round(newZoom * 100) / 100;
};

export const decrementZoom = (zoom: number) => {
  const newZoom = clamp(zoom - ZOOM_INCREMENT, MIN_ZOOM, MAX_ZOOM);
  return Math.round(newZoom * 100) / 100;
};

interface ZoomAtPointParams {
  anchor: Coords;
  rendererSize: Size;
  scroll: Scroll;
  oldZoom: number;
  newZoom: number;
}

/**
 * Returns the scroll position required to keep the same world point beneath
 * an on-screen anchor while the zoom changes.
 */
export const getZoomedScrollPosition = ({
  anchor,
  rendererSize,
  scroll,
  oldZoom,
  newZoom
}: ZoomAtPointParams): Coords => {
  if (oldZoom <= 0) return scroll.position;

  const worldX =
    (anchor.x - rendererSize.width / 2 - scroll.position.x) / oldZoom;
  const worldY =
    (anchor.y - rendererSize.height / 2 - scroll.position.y) / oldZoom;

  return {
    x: anchor.x - rendererSize.width / 2 - worldX * newZoom,
    y: anchor.y - rendererSize.height / 2 - worldY * newZoom
  };
};

/**
 * Converts wheel/trackpad movement to a proportional zoom. Exponential
 * scaling makes small trackpad deltas precise while keeping mouse wheels
 * responsive, independent of the current zoom level.
 */
export const getWheelZoom = (
  zoom: number,
  deltaY: number,
  deltaMode: number,
  viewportHeight: number
) => {
  const pixels = deltaMode === 1
    ? deltaY * 16
    : deltaMode === 2
      ? deltaY * viewportHeight
      : deltaY;
  const limitedDelta = clamp(pixels, -240, 240);
  const nextZoom = zoom * Math.exp(-limitedDelta * 0.002);

  return clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);
};

// ===================================================================
// MOUSE HANDLING
// ===================================================================

interface GetMouse {
  interactiveElement: HTMLElement;
  zoom: number;
  scroll: Scroll;
  lastMouse: Mouse;
  mouseEvent: SlimMouseEvent;
  rendererSize: Size;
}

export const getMouse = ({
  interactiveElement,
  zoom,
  scroll,
  lastMouse,
  mouseEvent,
  rendererSize
}: GetMouse): Mouse => {
  const componentOffset = interactiveElement.getBoundingClientRect();
  const offset: Coords = {
    x: componentOffset?.left ?? 0,
    y: componentOffset?.top ?? 0
  };

  const { clientX, clientY } = mouseEvent;

  const mousePosition = {
    x: clientX - offset.x,
    y: clientY - offset.y
  };

  const newPosition: Mouse['position'] = {
    screen: mousePosition,
    tile: screenToIso({
      mouse: mousePosition,
      zoom,
      scroll,
      rendererSize
    })
  };

  // On mousedown, reset delta to zero to prevent jump from previous position
  // On mousemove, calculate delta normally
  // This prevents the diagram from jumping when you first touch/click
  const newDelta: Mouse['delta'] = mouseEvent.type === 'mousedown'
    ? { screen: CoordsUtils.zero(), tile: CoordsUtils.zero() }
    : {
        screen: CoordsUtils.subtract(newPosition.screen, lastMouse.position.screen),
        tile: CoordsUtils.subtract(newPosition.tile, lastMouse.position.tile)
      };

  const getMousedown = (): Mouse['mousedown'] => {
    switch (mouseEvent.type) {
      case 'mousedown':
        return newPosition;
      case 'mousemove':
        return lastMouse.mousedown;
      default:
        return null;
    }
  };

  const nextMouse: Mouse = {
    position: newPosition,
    delta: newDelta,
    mousedown: getMousedown()
  };

  return nextMouse;
};

export const hasMovedTile = (mouse: Mouse) => {
  if (!mouse.delta) return false;
  return !CoordsUtils.isEqual(mouse.delta.tile, CoordsUtils.zero());
};

// ===================================================================
// TEXT BOX UTILITIES
// ===================================================================

export const getTextBoxEndTile = (textBox: TextBox, size: Size) => {
  if (textBox.orientation === ProjectionOrientationEnum.X) {
    return CoordsUtils.add(textBox.tile, {
      x: size.width,
      y: 0
    });
  }

  return CoordsUtils.add(textBox.tile, {
    x: 0,
    y: -size.width
  });
};

interface FontProps {
  fontWeight: number | string;
  fontSize: number;
  fontFamily: string;
}

export const getTextWidth = (text: string, fontProps: FontProps) => {
  if (!text) return 0;

  const paddingX = TEXTBOX_PADDING * UNPROJECTED_TILE_SIZE;
  const fontSizePx = toPx(fontProps.fontSize * UNPROJECTED_TILE_SIZE);
  const canvas: HTMLCanvasElement = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Could not get canvas context');
  }

  context.font = `${fontProps.fontWeight} ${fontSizePx} ${fontProps.fontFamily}`;
  const metrics = context.measureText(text);

  canvas.remove();

  return (metrics.width + paddingX * 2) / UNPROJECTED_TILE_SIZE - 0.8;
};

export const getTextBoxDimensions = (textBox: TextBox): Size => {
  const width = getTextWidth(textBox.content, {
    fontSize: textBox.fontSize ?? TEXTBOX_DEFAULTS.fontSize,
    fontFamily: DEFAULT_FONT_FAMILY,
    fontWeight: TEXTBOX_FONT_WEIGHT
  });
  const height = 1;

  return { width, height };
};

interface GetItemAtTileParams {
  tile: Coords;
  scene: ReturnType<typeof useScene>;
  excludeIds?: string[]; // Optional: IDs to exclude from search (useful when dragging)
}

const isTileOnConnectorSegment = (tile: Coords, from: Coords, to: Coords) => {
  const segmentX = to.x - from.x;
  const segmentY = to.y - from.y;
  const tileX = tile.x - from.x;
  const tileY = tile.y - from.y;
  const lengthSquared = segmentX ** 2 + segmentY ** 2;
  if (lengthSquared === 0) return CoordsUtils.isEqual(tile, from);

  const position = Math.max(0, Math.min(1,
    (tileX * segmentX + tileY * segmentY) / lengthSquared
  ));
  const nearestX = from.x + position * segmentX;
  const nearestY = from.y + position * segmentY;
  const distanceSquared = (tile.x - nearestX) ** 2 +
    (tile.y - nearestY) ** 2;

  // Include every tile crossed by a non-axis-aligned segment without making
  // neighbouring rows part of the connector's selection footprint.
  return distanceSquared <= 0.25;
};

export const getItemsAtTile = ({
  tile,
  scene,
  excludeIds = []
}: GetItemAtTileParams): ItemReference[] => {

  type HitCandidate = {
    ref: ItemReference;
    renderLayer: number;
    zIndex: number;
  };
  const candidates: HitCandidate[] = [];

  // Sort all elements by zIndex (highest first) to respect layer ordering
  // Higher zIndex = rendered on top = should be selected first
  const itemsByZIndex = [...scene.items]
    .filter((item) => !excludeIds.includes(item.id))
    .sort((a, b) => (b.zIndex ?? 0) - (a.zIndex ?? 0));
  const textBoxesByZIndex = [...scene.textBoxes]
    .filter((tb) => !excludeIds.includes(tb.id))
    .sort((a, b) => (b.zIndex ?? 0) - (a.zIndex ?? 0));
  const connectorsByZIndex = [...scene.connectors]
    .filter((c) => !excludeIds.includes(c.id))
    .sort((a, b) => (b.zIndex ?? 0) - (a.zIndex ?? 0));
  const rectanglesByZIndex = [...scene.rectangles]
    .filter((r) => !excludeIds.includes(r.id))
    .sort((a, b) => (b.zIndex ?? 0) - (a.zIndex ?? 0));

  // 1. Check for items (nodes) - sorted by zIndex
  itemsByZIndex
    .filter((item) => CoordsUtils.isEqual(item.tile, tile))
    .forEach((viewItem) => {
    candidates.push({
      ref: { type: 'ITEM', id: viewItem.id },
      renderLayer: RenderLayer.NODES,
      zIndex: viewItem.zIndex ?? 0
    });
    });

  // 2. Check for text boxes - sorted by zIndex
  textBoxesByZIndex.filter((tb) => {
    const textBoxTo = getTextBoxEndTile(tb, tb.size);
    const textBoxBounds = getBoundingBox([
      tb.tile,
      {
        x: Math.ceil(textBoxTo.x),
        y: tb.orientation === 'X' ? Math.ceil(textBoxTo.y) : Math.floor(textBoxTo.y)
      }
    ]);
    return isWithinBounds(tile, textBoxBounds);
  }).forEach((textBox) => {
    candidates.push({
      ref: { type: 'TEXTBOX', id: textBox.id },
      renderLayer: RenderLayer.TEXT_BOXES,
      zIndex: textBox.zIndex ?? 0
    });
  });

  // 3. ✅ CONNECTOR DETECTION - sorted by zIndex (highest first)
  // FIRST PASS: Check for direct path tile matches (exact clicks)
  for (const connector of connectorsByZIndex) {
    // Check anchors first
    // Endpoints attached to an item do not have ref.tile. Resolve every
    // anchor through the current view so attached endpoints remain draggable
    // instead of falling through to the connector-line waypoint behavior.
    const anchorAtTile = connector.anchors.find((anchor) =>
      CoordsUtils.isEqual(getAnchorTileCore(anchor, scene.currentView), tile)
    );
    if (anchorAtTile) {
      candidates.push({
        ref: { type: 'CONNECTOR_ANCHOR', id: anchorAtTile.id },
        // Anchors are interaction controls and intentionally beat ordinary scene content.
        renderLayer: RenderLayer.TRANSFORM_CONTROLS,
        zIndex: connector.zIndex ?? 0
      });
      continue;
    }

    // Check every segment, not only the declared anchor/waypoint tiles. The
    // path array contains the connector's bends, so point-only matching made
    // sections after the first waypoint impossible to select by tile.
    if (connector.path && connector.path.tiles && connector.path.tiles.length >= 2) {
      const globalPath = connector.path.tiles.map((pathTile) =>
        connectorPathTileToGlobalCore(pathTile, connector.path.rectangle.from)
      );
      const isOnPath = globalPath.slice(0, -1).some((pathTile, index) =>
        isTileOnConnectorSegment(tile, pathTile, globalPath[index + 1])
      );

      if (isOnPath) {
        candidates.push({
          ref: { type: 'CONNECTOR', id: connector.id },
          renderLayer: RenderLayer.CONNECTORS,
          zIndex: connector.zIndex ?? 0
        });
      }
    }
  }

  // 4. Check for rectangles - sorted by zIndex
  rectanglesByZIndex.filter((rect) => {
    const bounds = getBoundingBox([rect.from, rect.to]);
    return isWithinBounds(tile, bounds);
  }).forEach((rectangle) => {
    candidates.push({
      ref: { type: 'RECTANGLE', id: rectangle.id },
      renderLayer: RenderLayer.RECTANGLES,
      zIndex: rectangle.zIndex ?? 0
    });
  });

  // Cross-type order follows the renderer's fixed scene layers. zIndex only
  // resolves overlap between elements that share the same render layer.
  candidates.sort((a, b) =>
    b.renderLayer - a.renderLayer || b.zIndex - a.zIndex
  );

  return candidates.map((candidate) => candidate.ref);
};

export const getItemAtTile = (params: GetItemAtTileParams): ItemReference | null => {
  return getItemsAtTile(params)[0] ?? null;
};
//=====================================================
// ANCHOR/CONNECTOR UTILITIES
// ===================================================================

export const outermostCornerPositions: TileOrigin[] = [
  'BOTTOM',
  'RIGHT',
  'TOP',
  'LEFT'
];

export const convertBoundsToNamedAnchors = (
  boundingBox: BoundingBox
): {
    [key in AnchorPosition]: Coords;
  } => {
  return {
    BOTTOM_LEFT: boundingBox[0],
    BOTTOM_RIGHT: boundingBox[1],
    TOP_RIGHT: boundingBox[2],
    TOP_LEFT: boundingBox[3]
  };
};

export const getAnchorAtTile = (tile: Coords, anchors: ConnectorAnchor[]) => {
  return anchors.find((anchor) => {
    return Boolean(
      anchor.ref.tile && CoordsUtils.isEqual(anchor.ref.tile, tile)
    );
  });
};

export const getAnchorParent = (anchorId: string, connectors: Connector[]) => {
  const connector = connectors.find((con) => {
    return con.anchors.find((anchor) => {
      return anchor.id === anchorId;
    });
  });

  if (!connector) {
    throw new Error(`Could not find connector with anchor id ${anchorId}`);
  }

  return connector;
};

export const getTileScrollPosition = (
  tile: Coords,
  origin?: TileOrigin
): Coords => {
  const tilePosition = getTilePosition({ tile, origin });

  return {
    x: -tilePosition.x,
    y: -tilePosition.y
  };
};

export const getConnectorsByViewItem = (
  viewItemId: string,
  connectors: Connector[]
) => {
  return connectors.filter((connector) => {
    return connector.anchors.find((anchor) => {
      return anchor.ref.item === viewItemId;
    });
  });
};

export const getConnectorDirectionIcon = (connectorTiles: Coords[]) => {
  if (connectorTiles.length < 2) return null;

  const iconTile = connectorTiles[connectorTiles.length - 2];
  const lastTile = connectorTiles[connectorTiles.length - 1];

  let rotation;

  if (lastTile.x > iconTile.x) {
    if (lastTile.y > iconTile.y) {
      rotation = 135;
    } else if (lastTile.y < iconTile.y) {
      rotation = 45;
    } else {
      rotation = 90;
    }
  }

  if (lastTile.x < iconTile.x) {
    if (lastTile.y > iconTile.y) {
      rotation = -135;
    } else if (lastTile.y < iconTile.y) {
      rotation = -45;
    } else {
      rotation = -90;
    }
  }

  if (lastTile.x === iconTile.x) {
    if (lastTile.y > iconTile.y) {
      rotation = 180;
    } else if (lastTile.y < iconTile.y) {
      rotation = 0;
    } else {
      rotation = -90;
    }
  }

  return {
    x: iconTile.x * UNPROJECTED_TILE_SIZE + UNPROJECTED_TILE_SIZE / 2,
    y: iconTile.y * UNPROJECTED_TILE_SIZE + UNPROJECTED_TILE_SIZE / 2,
    rotation
  };
};

// ===================================================================
// PROJECT BOUNDS
// ===================================================================

export const getProjectBounds = (
  view: View,
  padding = PROJECT_BOUNDING_BOX_PADDING
): Coords[] => {
  const itemTiles = view.items.map((item) => {
    return item.tile;
  });

  const connectors = view.connectors ?? [];
  const connectorTiles = connectors.reduce<Coords[]>((acc, connector) => {
    const path = getConnectorPath({ anchors: connector.anchors, view });
    return [...acc, path.rectangle.from, path.rectangle.to];
  }, []);

  const rectangles = view.rectangles ?? [];
  const rectangleTiles = rectangles.reduce<Coords[]>((acc, rectangle) => {
    return [...acc, rectangle.from, rectangle.to];
  }, []);

  const textBoxes = view.textBoxes ?? [];
  const textBoxTiles = textBoxes.reduce<Coords[]>((acc, textBox) => {
    const size = getTextBoxDimensions(textBox);

    return [
      ...acc,
      textBox.tile,
      CoordsUtils.add(textBox.tile, {
        x: size.width,
        y: size.height
      })
    ];
  }, []);

  let allTiles = [
    ...itemTiles,
    ...connectorTiles,
    ...rectangleTiles,
    ...textBoxTiles
  ];

  if (allTiles.length === 0) {
    const centerTile = CoordsUtils.zero();
    allTiles = [centerTile, centerTile, centerTile, centerTile];
  }

  const corners = getBoundingBox(allTiles, {
    x: padding,
    y: padding
  });

  return corners;
};

export const getUnprojectedBounds = (view: View) => {
  const projectBounds = getProjectBounds(view);

  const cornerPositions = projectBounds.map((corner) => {
    return getTilePosition({
      tile: corner
    });
  });
  const sortedCorners = sortByPosition(cornerPositions);
  const topLeft = { x: sortedCorners.lowX, y: sortedCorners.lowY };
  // These are projected pixel positions, not tile indexes. getBoundingBoxSize
  // includes an extra unit for inclusive tile ranges, which makes the center
  // and fit calculations subtly incorrect here.
  const size = {
    width: sortedCorners.highX - sortedCorners.lowX,
    height: sortedCorners.highY - sortedCorners.lowY
  };

  return {
    width: size.width,
    height: size.height,
    x: topLeft.x,
    y: topLeft.y
  };
};

/**
 * Calculates a tight projected bounding box from geometry that is actually
 * visible. Unlike the project/export bounds, this does not create imaginary
 * corners from the complete tile-space AABB.
 */
export const getFitToViewBounds = (view: View) => {
  const visibleItems = view.items.filter((item) => item.visible !== false);
  const itemTiles = visibleItems.map((item) => item.tile);
  const itemLabelAnchors = visibleItems.map((item) => {
    const position = getTilePosition({ tile: item.tile });
    return {
      x: position.x,
      y: position.y - Math.max(item.labelHeight ?? DEFAULT_LABEL_HEIGHT, 0)
    };
  });

  const connectorTiles = (view.connectors ?? [])
    .filter((connector) => connector.visible !== false)
    .flatMap((connector) => connector.anchors.map((anchor) => {
      return getAnchorTileCore(anchor, view);
    }));

  const rectangleTiles = (view.rectangles ?? [])
    .filter((rectangle) => rectangle.visible !== false)
    .flatMap((rectangle) => [
      rectangle.from,
      { x: rectangle.from.x, y: rectangle.to.y },
      rectangle.to,
      { x: rectangle.to.x, y: rectangle.from.y }
    ]);

  const textBoxTiles = (view.textBoxes ?? [])
    .filter((textBox) => textBox.visible !== false)
    .flatMap((textBox) => {
      const size = getTextBoxDimensions(textBox);
      return [textBox.tile, getTextBoxEndTile(textBox, size)];
    });

  const contentTiles = [
    ...itemTiles,
    ...connectorTiles,
    ...rectangleTiles,
    ...textBoxTiles
  ];
  const projectedTilePositions = (contentTiles.length > 0
    ? contentTiles
    : [CoordsUtils.zero()]
  ).map((tile) => getTilePosition({ tile }));
  const projectedPositions = [
    ...projectedTilePositions,
    ...itemLabelAnchors
  ];
  const positions = sortByPosition(projectedPositions);
  const paddingX = PROJECTED_TILE_SIZE.width * FIT_TO_VIEW_TILE_PADDING;
  const paddingY = PROJECTED_TILE_SIZE.height * FIT_TO_VIEW_TILE_PADDING;

  return {
    x: positions.lowX - paddingX,
    y: positions.lowY - paddingY,
    width: positions.highX - positions.lowX + paddingX * 2,
    height: positions.highY - positions.lowY + paddingY * 2
  };
};

export const getFitToViewParams = (view: View, viewportSize: Size) => {
  const bounds = getFitToViewBounds(view);
  const safeWidth = Math.max(bounds.width, 1);
  const safeHeight = Math.max(bounds.height, 1);
  const availableWidth = Math.max(viewportSize.width, 1);
  const availableHeight = Math.max(viewportSize.height, 1);

  const zoom = clamp(
    Math.min(
      availableWidth / safeWidth,
      availableHeight / safeHeight
    ),
    MIN_ZOOM,
    MAX_ZOOM
  );
  const contentCenter = {
    x: bounds.x + bounds.width / 2,
    y: bounds.y + bounds.height / 2
  };
  const scroll = {
    x: -contentCenter.x * zoom,
    y: -contentCenter.y * zoom
  };

  return {
    zoom,
    scroll
  };
};
